import React, { Dispatch, Fragment, SetStateAction, useState } from 'react'
import { Calendar, DateRange, Range, RangeKeyDict } from 'react-date-range'
import dayjs from 'dayjs'
import { createPortal } from 'react-dom'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from '../Buttons'
import { TestIds } from '../../../Utils/testIds'
import {
  BlurBackground,
  DateModalBody,
  DateModalFooter,
  DateRangeEditButton,
  DateRangeModalContainer,
  DateRangeWrapper,
  TimeInput,
  TimeField,
  TimeInputWrapper,
} from './TrusteroDateRange.styles'

// Workaround for React 18 compatibility issues https://github.com/styled-components/styled-components/issues/3738
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CalendarProxy: any = Calendar
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DateRangeProxy: any = DateRange

export const DateRangeModal = ({
  single,
  range,
  rangeSet,
  editingSet,
  dateDisplayFormat,
  enforcedOffsetDays,
  showTime,
}: {
  single?: boolean
  range: Range
  rangeSet: Dispatch<SetStateAction<Range>>
  editingSet: Dispatch<SetStateAction<boolean>>
  dateDisplayFormat?: string
  enforcedOffsetDays?: number
  showTime?: boolean
}): JSX.Element => {
  const [tempRange, tempRangeSet] = useState(range)
  // Start date time default to 9 AM
  const [startTime, setStartTime] = useState(
    dayjs(range.startDate).format('HH:mm') === '00:00'
      ? '09:00'
      : dayjs(range.startDate).format('HH:mm'),
  )
  // End date time default to 5 PM
  const [endTime, setEndTime] = useState(
    dayjs(range.endDate).format('HH:mm') === '23:59' ||
      dayjs(range.endDate).format('HH:mm') === '00:00'
      ? '17:00'
      : dayjs(range.endDate).format('HH:mm'),
  )
  const defaultMinDate = new Date(2020, 1, 1, 23, 59, 59, 999)

  const disabledDay = (day: Date) => {
    if (!enforcedOffsetDays) return false
    const { startDate, endDate } = tempRange

    // If no range yet, allow everything
    if (!startDate || !endDate) return false

    if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
      // Disable days within the enforced offset days
      return (
        (dayjs(day).isBefore(dayjs(startDate)) &&
          !dayjs(day).isBefore(
            dayjs(startDate).subtract(enforcedOffsetDays, 'days'),
          )) ||
        (dayjs(day).isAfter(dayjs(startDate)) &&
          !dayjs(day).isAfter(
            dayjs(startDate).add(enforcedOffsetDays - 1, 'days'),
          ))
      )
    }

    return false
  }

  return (
    <>
      {createPortal(
        <BlurBackground />,
        document.getElementsByTagName('body')[0],
      )}
      {createPortal(
        <DateRangeModalContainer>
          <DateModalBody>
            {single ? (
              <CalendarProxy
                showDateDisplay={true}
                editableDateInputs
                ranges={[tempRange]}
                date={tempRange.endDate as Date}
                minDate={defaultMinDate}
                startDatePlaceholder="Select Date"
                endDatePlaceholder="Select Date"
                onChange={(newRange: Date) => {
                  const [sh, sm] = (startTime || '').split(':').map(Number)
                  const [eh, em] = (endTime || '').split(':').map(Number)

                  const startTimeValid =
                    Number.isFinite(sh) && Number.isFinite(sm)
                  const endTimeValid =
                    Number.isFinite(eh) && Number.isFinite(em)

                  const startH = startTimeValid
                    ? sh
                    : dayjs(tempRange.startDate).hour()
                  const startM = startTimeValid
                    ? sm
                    : dayjs(tempRange.startDate).minute()
                  const endH = endTimeValid
                    ? eh
                    : dayjs(tempRange.endDate).hour()
                  const endM = endTimeValid
                    ? em
                    : dayjs(tempRange.endDate).minute()

                  tempRangeSet({
                    // When type 1 audit, will default to 90 day windows ending
                    // with selected date
                    startDate: dayjs(newRange)
                      .subtract(90, 'days')
                      .hour(startH)
                      .minute(startM)
                      .second(0)
                      .millisecond(0)
                      .toDate(),
                    endDate: dayjs(newRange)
                      .hour(endH)
                      .minute(endM)
                      .second(0)
                      .millisecond(0)
                      .toDate(),
                  })
                }}
                dateDisplayFormat={dateDisplayFormat}
              />
            ) : (
              <DateRangeProxy
                editableDateInputs
                ranges={[tempRange]}
                minDate={defaultMinDate}
                months={2}
                disabledDay={disabledDay}
                direction="horizontal"
                startDatePlaceholder="Select Start Date"
                endDatePlaceholder="Select End Date"
                onChange={({ range1 }: RangeKeyDict) => {
                  const [sh, sm] = (startTime || '').split(':').map(Number)
                  const [eh, em] = (endTime || '').split(':').map(Number)

                  const startTimeValid =
                    Number.isFinite(sh) && Number.isFinite(sm)
                  const endTimeValid =
                    Number.isFinite(eh) && Number.isFinite(em)

                  if (range1.startDate) {
                    const startH = startTimeValid
                      ? sh
                      : dayjs(tempRange.startDate).hour()
                    const startM = startTimeValid
                      ? sm
                      : dayjs(tempRange.startDate).minute()

                    range1.startDate = dayjs(range1.startDate)
                      .hour(startH)
                      .minute(startM)
                      .second(0)
                      .millisecond(0)
                      .toDate()
                  }
                  if (range1.endDate) {
                    const endH = endTimeValid
                      ? eh
                      : dayjs(tempRange.endDate).hour()
                    const endM = endTimeValid
                      ? em
                      : dayjs(tempRange.endDate).minute()

                    range1.endDate = dayjs(range1.endDate)
                      .hour(endH)
                      .minute(endM)
                      .second(0)
                      .millisecond(0)
                      .toDate()
                  }
                  tempRangeSet(range1)
                }}
                dateDisplayFormat={dateDisplayFormat}
              />
            )}
            {showTime && (
              <TimeInputWrapper>
                {!single && (
                  <TimeField>
                    <label>Start Time</label>
                    <TimeInput
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </TimeField>
                )}
                <TimeField>
                  <label>{single ? 'Time' : 'End Time'}</label>
                  <TimeInput
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </TimeField>
              </TimeInputWrapper>
            )}
            <DateModalFooter>
              <StandardButton
                variant={StandardButtonVariant.SECONDARY}
                buttonSize={StandardButtonSize.MEDIUM}
                mr="m"
                onClick={() => editingSet(false)}
              >
                Cancel
              </StandardButton>
              <StandardButton
                variant={StandardButtonVariant.PRIMARY}
                buttonSize={StandardButtonSize.MEDIUM}
                onClick={() => {
                  const finalRange = { ...tempRange }
                  if (showTime) {
                    const [endH, endM] = endTime.split(':').map(Number)
                    const [startH, startM] = startTime.split(':').map(Number)

                    if (
                      finalRange.startDate &&
                      Number.isFinite(startH) &&
                      Number.isFinite(startM)
                    ) {
                      finalRange.startDate = dayjs(finalRange.startDate)
                        .hour(startH)
                        .minute(startM)
                        .second(0)
                        .millisecond(0)
                        .toDate()
                    }
                    if (
                      finalRange.endDate &&
                      Number.isFinite(endH) &&
                      Number.isFinite(endM)
                    ) {
                      finalRange.endDate = dayjs(finalRange.endDate)
                        .hour(endH)
                        .minute(endM)
                        .second(0)
                        .millisecond(0)
                        .toDate()
                    }
                  }
                  rangeSet(finalRange)
                  editingSet(false)
                }}
              >
                Save
              </StandardButton>
            </DateModalFooter>
          </DateModalBody>
        </DateRangeModalContainer>,
        document.getElementsByTagName('body')[0],
      )}
    </>
  )
}

export const TrusteroDateRange = ({
  range,
  rangeSet,
  disabled,
  single,
  dateDisplayFormat,
  width,
  showTime,
}: {
  range: Range
  rangeSet: Dispatch<SetStateAction<Range>>
  disabled?: boolean
  single?: boolean
  dateDisplayFormat?: string
  width?: string
  showTime?: boolean
}): JSX.Element => {
  const [editing, editingSet] = useState(false)

  const displayFormat =
    dateDisplayFormat || (showTime ? 'MMM d, yyyy HH:mm' : 'MMM d, yyyy')

  return (
    <DateRangeWrapper disabled={disabled} $width={width} $single={single}>
      <>
        {!disabled && (
          <DateRangeEditButton
            data-testid={TestIds.DATE_RANGE_EDIT_BUTTON}
            onClick={(e) => {
              e.preventDefault()
              editingSet(true)
            }}
          />
        )}
        <DateRangeProxy
          ranges={[{ ...range, disabled: true, showDateDisplay: true }]}
          months={0}
          startDatePlaceholder="Select Date"
          endDatePlaceholder="Select Date"
          navigatorRenderer={() => <Fragment />}
          dateDisplayFormat={displayFormat}
        />
        {editing && (
          <DateRangeModal
            single={single}
            range={range}
            rangeSet={rangeSet}
            editingSet={editingSet}
            dateDisplayFormat={displayFormat}
            showTime={showTime}
          />
        )}
      </>
    </DateRangeWrapper>
  )
}
